import axios from "axios";

import { API_BASE_URL } from "../../../config/api";
import {
  ADD_ITEM_TO_CART_REQUEST,
  ADD_ITEM_TO_CART_SUCCESS,
  ADD_ITEM_TO_CART_FAILURE,
  GET_CART_FAILURE,
  GET_CART_REQUEST,
  GET_CART_SUCCESS,
  REMOVE_CART_ITEM_FAILURE,
  REMOVE_CART_ITEM_REQUEST,
  REMOVE_CART_ITEM_SUCCESS,
  UPDATE_CART_ITEM_FAILURE,
  UPDATE_CART_ITEM_REQUEST,
  UPDATE_CART_ITEM_SUCCESS,
  // Guest Cart Action Types
  LOAD_GUEST_CART,
  ADD_GUEST_CART_ITEM,
  REMOVE_GUEST_CART_ITEM,
  UPDATE_GUEST_CART_ITEM,
  CLEAR_GUEST_CART,
  BULK_LIMIT_EXCEEDED,
  // Merge Action Types
  MERGE_CART_REQUEST,
  MERGE_CART_SUCCESS,
  MERGE_CART_FAILURE,
  // Clear User Cart Action Types
  CLEAR_USER_CART_REQUEST,
  CLEAR_USER_CART_SUCCESS,
  CLEAR_USER_CART_FAILURE,
} from "./ActionType";

import {
  getGuestCart,
  addGuestCartItem as addGuestItem,
  removeGuestCartItemById,
  updateGuestCartItemById,
  clearGuestCart as clearGuestStorage,
  getGuestCartForMerge,
  getGuestCartTotals,
} from "../../../utils/CartSync";

// ============================================
// AUTHENTICATED CART ACTIONS (Existing - Unchanged)
// ============================================

export const addItemToCart = (reqData) => async (dispatch) => {
  try {
    dispatch({ type: ADD_ITEM_TO_CART_REQUEST });
    const config = {
      headers: {
        Authorization: `Bearer ${reqData.jwt}`,
        "Content-Type": "application/json",
      },
    };
    const { data } = await axios.put(
      `${API_BASE_URL}/api/cart/add`,
      reqData.data,
      config
    );
    dispatch({
      type: ADD_ITEM_TO_CART_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: ADD_ITEM_TO_CART_FAILURE,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const getCart = (jwt) => async (dispatch) => {
  try {
    dispatch({ type: GET_CART_REQUEST });
    const config = {
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    };
    const { data } = await axios.get(`${API_BASE_URL}/api/cart/`, config);
    dispatch({
      type: GET_CART_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: GET_CART_FAILURE,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const removeCartItem = (reqData) => async (dispatch) => {
  try {
    dispatch({ type: REMOVE_CART_ITEM_REQUEST });
    const config = {
      headers: {
        Authorization: `Bearer ${reqData.jwt}`,
        "Content-Type": "application/json",
      },
    };
    await axios.delete(
      `${API_BASE_URL}/api/cart_items/${reqData.cartItemId}`,
      config
    );

    dispatch({
      type: REMOVE_CART_ITEM_SUCCESS,
      payload: reqData.cartItemId,
    });
  } catch (error) {
    dispatch({
      type: REMOVE_CART_ITEM_FAILURE,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const updateCartItem = (reqData) => async (dispatch) => {
  try {
    dispatch({ type: UPDATE_CART_ITEM_REQUEST });
    const config = {
      headers: {
        Authorization: `Bearer ${reqData.jwt}`,
        "Content-Type": "application/json",
      },
    };
    const { data } = await axios.put(
      `${API_BASE_URL}/api/cart_items/${reqData.cartItemId}`,
      reqData.data,
      config
    );
    dispatch({
      type: UPDATE_CART_ITEM_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: UPDATE_CART_ITEM_FAILURE,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

// ============================================
// GUEST CART ACTIONS (Local-only, sessionStorage)
// ============================================

/**
 * Load guest cart from sessionStorage into Redux state
 */
export const loadGuestCart = () => (dispatch) => {
  const guestCartItems = getGuestCart();
  const totals = getGuestCartTotals();

  dispatch({
    type: LOAD_GUEST_CART,
    payload: {
      guestCartItems,
      ...totals,
    },
  });
};

/**
 * Add item to guest cart (sessionStorage + Redux)
 * @param {Object} item - { productId, size, quantity, product }
 */
export const addToGuestCart = (item) => (dispatch) => {
  const result = addGuestItem(item);

  if (!result.success && result.error === 'BULK_LIMIT_EXCEEDED') {
    dispatch({
      type: BULK_LIMIT_EXCEEDED,
      payload: result.message,
    });
    return;
  }

  const totals = getGuestCartTotals();
  dispatch({
    type: ADD_GUEST_CART_ITEM,
    payload: {
      guestCartItems: result.cart,
      ...totals,
    },
  });
};

/**
 * Remove item from guest cart by ID
 * @param {string} itemId - Guest cart item ID
 */
export const removeFromGuestCart = (itemId) => (dispatch) => {
  const updatedCart = removeGuestCartItemById(itemId);
  const totals = getGuestCartTotals();

  dispatch({
    type: REMOVE_GUEST_CART_ITEM,
    payload: {
      guestCartItems: updatedCart,
      ...totals,
    },
  });
};

/**
 * Update guest cart item quantity
 * @param {string} itemId - Guest cart item ID
 * @param {number} quantity - New quantity
 */
export const updateGuestCartItemQuantity = (itemId, quantity) => (dispatch) => {
  const result = updateGuestCartItemById(itemId, quantity);

  if (!result.success && result.error === 'BULK_LIMIT_EXCEEDED') {
    dispatch({
      type: BULK_LIMIT_EXCEEDED,
      payload: result.message,
    });
    return;
  }

  const totals = getGuestCartTotals();
  dispatch({
    type: UPDATE_GUEST_CART_ITEM,
    payload: {
      guestCartItems: result.cart,
      ...totals,
    },
  });
};

/**
 * Clear guest cart (used after successful merge)
 */
export const clearGuestCart = () => (dispatch) => {
  clearGuestStorage();
  dispatch({ type: CLEAR_GUEST_CART });
};

// ============================================
// CART MERGE ACTIONS (Post-authentication)
// ============================================

/**
 * Merge guest cart into authenticated user's cart
 * Called immediately after login/registration
 * @param {string} jwt - JWT token
 */
export const mergeGuestCart = (jwt) => async (dispatch) => {
  const guestItems = getGuestCartForMerge();

  // If no guest items, skip merge
  if (!guestItems || guestItems.length === 0) {
    return;
  }

  try {
    dispatch({ type: MERGE_CART_REQUEST });

    const config = {
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    };

    const { data } = await axios.post(
      `${API_BASE_URL}/api/cart/merge`,
      { items: guestItems },
      config
    );


    // Only clear guest cart if merge was successful
    clearGuestStorage();

    dispatch({
      type: MERGE_CART_SUCCESS,
      payload: data,
    });


    // Dispatch to clear guest cart from Redux
    dispatch({ type: CLEAR_GUEST_CART });

  } catch (error) {
    console.error("Cart merge failed", error);
    dispatch({
      type: MERGE_CART_FAILURE,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};

/**
 * Smart add to cart - routes to guest or authenticated cart based on JWT
 * @param {Object} reqData - { data: { productId, size, quantity }, jwt (optional), product }
 */
export const smartAddToCart = (reqData) => async (dispatch) => {
  const jwt = reqData.jwt || localStorage.getItem("jwt");

  if (jwt) {
    // Authenticated: Use backend cart
    return dispatch(addItemToCart({ ...reqData, jwt }));
  } else {
    // Guest: Use sessionStorage cart
    return dispatch(addToGuestCart({
      productId: reqData.data.productId,
      size: reqData.data.size,
      quantity: reqData.data.quantity,
      product: reqData.product,
      pricePerLb: reqData.product?.price,
      discountedPricePerLb: reqData.product?.discountedPrice,
    }));
  }
};

/**
 * Smart remove from cart - routes based on JWT and item type
 * @param {Object} reqData - { cartItemId, jwt (optional), isGuest }
 */
export const smartRemoveFromCart = (reqData) => async (dispatch) => {
  const jwt = reqData.jwt || localStorage.getItem("jwt");

  if (reqData.isGuest || !jwt) {
    return dispatch(removeFromGuestCart(reqData.cartItemId));
  } else {
    return dispatch(removeCartItem({ ...reqData, jwt }));
  }
};

/**
 * Smart update cart item - routes based on JWT and item type
 * @param {Object} reqData - { cartItemId, data, jwt (optional), isGuest }
 */
export const smartUpdateCartItem = (reqData) => async (dispatch) => {
  const jwt = reqData.jwt || localStorage.getItem("jwt");

  if (reqData.isGuest || !jwt) {
    return dispatch(updateGuestCartItemQuantity(reqData.cartItemId, reqData.data.quantity));
  } else {
    return dispatch(updateCartItem({ ...reqData, jwt }));
  }
};

// ============================================
// CLEAR USER CART (After order placed)
// ============================================

/**
 * Clear authenticated user's cart after order is placed
 * @param {string} jwt - JWT token
 */
export const clearUserCart = (jwt) => async (dispatch) => {
  try {
    dispatch({ type: CLEAR_USER_CART_REQUEST });

    const config = {
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    };

    await axios.delete(`${API_BASE_URL}/api/cart/clear`, config);


    dispatch({ type: CLEAR_USER_CART_SUCCESS });

    // Also clear guest cart if any
    clearGuestStorage();
    dispatch({ type: CLEAR_GUEST_CART });

  } catch (error) {
    console.error("Clear cart failed", error);
    dispatch({
      type: CLEAR_USER_CART_FAILURE,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
  }
};
